/**
 * ColdCallScriptModal — AI-generated cold-call script for a discovered buyer.
 *
 * Pops over the buyer card. Calls POST /api/buyer-search/script which uses
 * Claude Sonnet 4.5 with all the Tier 1 + Tier 2 intel to produce a personalized
 * 4-section script (Opening / Value Hook / Discovery Question / Soft CTA).
 *
 * Features:
 *   - Loading state with a clear "Claude is writing…" indicator
 *   - Markdown-ish rendering (section headers in emerald)
 *   - Copy-to-clipboard button
 *   - Regenerate button (re-rolls the script)
 *   - Optional lead context: if `lead` prop is passed, the script will pitch
 *     that specific deal instead of a generic buyer-list.
 */
import { useEffect, useState, useCallback } from "react";
import { Loader2, Copy, RefreshCw, X, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";


function renderScript(text) {
  // Tiny markdown renderer: ## headings become styled, paragraphs become <p>
  const lines = text.split("\n");
  const out = [];
  let buf = [];
  const flush = () => {
    if (buf.length) {
      out.push(
        <p key={`p-${out.length}`} className="text-sm text-[var(--ink)] leading-relaxed mb-3 whitespace-pre-wrap">
          {buf.join("\n")}
        </p>
      );
      buf = [];
    }
  };
  for (const raw of lines) {
    const line = raw.trim();
    if (line.startsWith("## ")) {
      flush();
      out.push(
        <h4
          key={`h-${out.length}`}
          className="font-display font-bold text-xs uppercase tracking-widest text-emerald-700 mt-4 first:mt-0 mb-1.5"
        >
          {line.slice(3)}
        </h4>
      );
    } else if (!line) {
      flush();
    } else {
      buf.push(raw);
    }
  }
  flush();
  return out;
}


export default function ColdCallScriptModal({ open, onClose, buyer, lead }) {
  const [loading, setLoading] = useState(false);
  const [script, setScript] = useState("");
  const [copied, setCopied] = useState(false);

  const generate = useCallback(async () => {
    if (!buyer) return;
    setLoading(true);
    setScript("");
    try {
      const payload = { buyer };
      if (lead) payload.lead = lead;
      const r = await api.post("/buyer-search/script", payload, { timeout: 60000 });
      setScript(r.data?.script || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate script");
      onClose?.();
    } finally {
      setLoading(false);
    }
  }, [buyer, lead, onClose]);

  // Auto-generate on open
  useEffect(() => {
    if (open && buyer) {
      generate();
      setCopied(false);
    }
  }, [open, buyer?.id, generate]);

  // Escape key closes
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !buyer) return null;

  const handleCopy = async () => {
    if (!script) return;
    // Primary path: modern async clipboard API
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(script);
        setCopied(true);
        toast.success("Script copied");
        setTimeout(() => setCopied(false), 2000);
        return;
      }
    } catch {
      // Fall through to legacy method
    }
    // Fallback for older / locked-down browsers (headless, iOS Safari, etc.)
    try {
      const ta = document.createElement("textarea");
      ta.value = script;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (ok) {
        setCopied(true);
        toast.success("Script copied");
        setTimeout(() => setCopied(false), 2000);
      } else {
        throw new Error("execCommand failed");
      }
    } catch {
      toast.error("Copy failed — select the text manually");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="cold-call-script-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-5 border-b border-[var(--line)]">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 flex items-center gap-1">
              <Sparkles size={11} /> AI Cold-Call Script · Claude Sonnet 4.5
            </div>
            <h3 className="font-display font-bold text-xl truncate mt-0.5">
              {buyer.name}
            </h3>
            <div className="text-xs text-[var(--ink-3)] mt-0.5">
              {buyer.primary_market}
              {buyer.properties_owned ? ` · ${buyer.properties_owned} properties` : ""}
              {buyer.is_millionaire ? " · Millionaire" : ""}
              {lead?.property_address ? ` · Pitching ${lead.property_address}` : ""}
            </div>
          </div>
          <button
            type="button"
            className="p-1.5 rounded-md hover:bg-[var(--bg-2)] text-[var(--ink-3)]"
            onClick={onClose}
            data-testid="script-modal-close"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div
          className="flex-1 overflow-y-auto p-5"
          data-testid="script-modal-body"
        >
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Loader2 size={32} className="animate-spin text-emerald-600 mb-3" />
              <div className="text-sm font-bold text-[var(--ink)]">
                Claude is drafting your opener…
              </div>
              <div className="text-xs text-[var(--ink-3)] mt-1">
                Personalized using all wealth + deed signals (~10s)
              </div>
            </div>
          ) : script ? (
            <div data-testid="script-modal-content">{renderScript(script)}</div>
          ) : (
            <div className="text-sm text-[var(--ink-3)] text-center py-16">
              No script yet. Click regenerate to try again.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 p-4 border-t border-[var(--line)] bg-[var(--bg-1)] rounded-b-2xl">
          <div className="text-[10px] text-[var(--ink-3)]">
            Tip: replace [YOUR NAME] before dialing.
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn-outline text-xs"
              onClick={generate}
              disabled={loading}
              data-testid="script-regenerate"
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
              Regenerate
            </button>
            <button
              type="button"
              className="btn-money text-xs"
              onClick={handleCopy}
              disabled={!script || loading}
              data-testid="script-copy"
            >
              {copied ? <CheckCircle2 size={12} /> : <Copy size={12} />}
              {copied ? "Copied" : "Copy script"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
